import React, { useEffect, useState } from "react";
import { Modal } from "react-bootstrap";
import { useUpdateContract } from "../../api/contracts";
import { useToast } from "../../components/ToastProvider";
import { formatBRL } from "../../utils/money";
import { Contract } from "../../types/dtos";

type Mode = "percent" | "fixed";

export function ReadjustContractModal({ contract, onClose }: { contract: Contract | null; onClose: () => void }) {
  const [mode, setMode] = useState<Mode>("percent");
  const [percent, setPercent] = useState("");
  const [fixedValue, setFixedValue] = useState("");
  const [readjustmentIndex, setReadjustmentIndex] = useState<"IPCA" | "IGPM" | "NONE">("NONE");
  const [error, setError] = useState<string | null>(null);

  const update = useUpdateContract();
  const toast = useToast();

  useEffect(() => {
    if (contract) {
      setMode("percent");
      setPercent("");
      setFixedValue(String(contract.monthlyValue));
      setReadjustmentIndex(contract.readjustmentIndex);
      setError(null);
    }
  }, [contract]);

  if (!contract) return null;

  const currentValue = Number(contract.monthlyValue);
  const percentNumber = Number(percent.replace(",", "."));
  const fixedNumber = Number(fixedValue.replace(",", "."));
  const newValue = mode === "percent" ? currentValue * (1 + (Number.isFinite(percentNumber) ? percentNumber : 0) / 100) : fixedNumber;

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!contract) return;
    if (!Number.isFinite(newValue) || newValue < 0) {
      setError("Informe um percentual ou valor válido.");
      return;
    }
    try {
      await update.mutateAsync({
        id: contract.id,
        input: { monthlyValue: Number(newValue.toFixed(2)), readjustmentIndex },
      });
      toast(`Mensalidade de ${contract.code} reajustada para ${formatBRL(newValue)}.`);
      onClose();
    } catch {
      setError("Não foi possível aplicar o reajuste.");
    }
  }

  return (
    <Modal backdrop="static" keyboard={false} show onHide={onClose} centered className="nt-dialog">
      <form onSubmit={handleSubmit}>
        <Modal.Header closeButton closeVariant="white">
          <Modal.Title style={{ fontSize: 20, fontFamily: "var(--font-heading)" }}>
            Reajustar {contract.code}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className="d-flex flex-column gap-3">
          <div style={{ fontSize: 13 }} className="text-secondary">
            {contract.customer.razaoSocial} — {contract.plan.application.name} / {contract.plan.name}
            <br />
            Valor atual: <strong style={{ color: "var(--color-text)" }}>{formatBRL(currentValue)}</strong>
          </div>

          <div className="d-flex gap-3">
            <div className="form-check">
              <input
                className="form-check-input"
                type="radio"
                id="mode-percent"
                checked={mode === "percent"}
                onChange={() => setMode("percent")}
              />
              <label className="form-check-label" htmlFor="mode-percent" style={{ fontSize: 13 }}>
                Por percentual
              </label>
            </div>
            <div className="form-check">
              <input
                className="form-check-input"
                type="radio"
                id="mode-fixed"
                checked={mode === "fixed"}
                onChange={() => setMode("fixed")}
              />
              <label className="form-check-label" htmlFor="mode-fixed" style={{ fontSize: 13 }}>
                Novo valor direto
              </label>
            </div>
          </div>

          {mode === "percent" ? (
            <div>
              <label className="form-label" style={{ fontSize: 12 }}>
                Percentual de reajuste (%)
              </label>
              <input
                className="form-control"
                value={percent}
                onChange={(e) => setPercent(e.target.value)}
                placeholder="Ex.: 4,62 (IPCA acumulado)"
              />
            </div>
          ) : (
            <div>
              <label className="form-label" style={{ fontSize: 12 }}>
                Novo valor da mensalidade (R$)
              </label>
              <input className="form-control" value={fixedValue} onChange={(e) => setFixedValue(e.target.value)} />
            </div>
          )}

          <div>
            <label className="form-label" style={{ fontSize: 12 }}>
              Índice de reajuste
            </label>
            <select
              className="form-select"
              value={readjustmentIndex}
              onChange={(e) => setReadjustmentIndex(e.target.value as "IPCA" | "IGPM" | "NONE")}
            >
              <option value="NONE">Nenhum</option>
              <option value="IPCA">IPCA</option>
              <option value="IGPM">IGP-M</option>
            </select>
          </div>

          <div className="p-2" style={{ background: "var(--color-surface-2)", borderRadius: "var(--radius-md)", fontSize: 13 }}>
            Nova mensalidade: <strong>{Number.isFinite(newValue) ? formatBRL(newValue) : "—"}</strong>
          </div>
          <p className="mb-0 text-secondary" style={{ fontSize: 11.5 }}>
            Vale a partir da próxima cobrança gerada para este contrato — não altera títulos já emitidos.
          </p>

          {error && (
            <div className="text-danger" style={{ fontSize: 12.5 }}>
              {error}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <button type="button" className="btn btn-danger" onClick={onClose}>
            Cancelar
          </button>
          <button type="submit" className="btn btn-success" disabled={update.isPending}>
            Aplicar reajuste
          </button>
        </Modal.Footer>
      </form>
    </Modal>
  );
}
